import { DownloadOutlined } from "@ant-design/icons";
import { ReactComponent as Alert } from "@assets/redesign/icons/alert.svg";
import Loader from "@components/global/Loader/Loader";
import Counter from "@components/global/new/Counter/Counter";
import SmallLink from "@components/global/new/SmallLink/SmallLink";
import cn from "classnames";
import type { CSSProperties } from "react";
import { useIntl } from "react-intl";
import { Element } from "react-scroll";
import AddNewMiner from "../AddNewMiner/AddNewMiner";
import styles from "../Machines.module.scss";
import MinerContextProvider from "../MinerRow/MinerContext";
import MinerRow from "../MinerRow/MinerRow";
import UnknownMinerRow, { UnknownTableHeader } from "../MinerRow/UnknownMinerRow";
import RefreshMiners from "../RefreshMiners/RefreshMiners";
import TableHeader from "./Header/TableHeader";
import type { useMinersTableControl } from "./useMinersTableControl";

type MinersTableControl = ReturnType<typeof useMinersTableControl>;

export interface TableItemStyles {
  readonly checkboxStyle: CSSProperties;
  readonly rowsStyles: CSSProperties[];
}

interface DesktopContentProps {
  readonly control: MinersTableControl;
  readonly tableStyles?: TableItemStyles;
  readonly total: number;
}

export function DesktopContent({
  control,
  tableStyles,
  total,
}: DesktopContentProps) {
  const { formatMessage } = useIntl();

  if (control.tab === "unknown")
    return (
      <>
        {control.unknownMiners.map((miner) => {
          if (!miner) return <></>;
          return <UnknownMinerRow key={miner.id} miner={miner} />;
        })}
      </>
    );

  if (control.miners.length < 1 && !control.loading) {
    return (
      <div className={styles.empty_minerList}>
        {formatMessage({ id: "machines.table.empty" })}
      </div>
    );
  } else
    return (
      <div className={styles.table}>
        {control.miners.map((miner, i) => {
          if (!miner) return <></>;
          const page = control.page || 1;
          const pageSize = control.pageSize || 1;
          const currentNum =
            page === 1 ? i + 1 : (page - 1) * pageSize + i + 1;

          return (
            <Counter
              className={styles.counter}
              count={Math.min(currentNum, total)}
              key={miner.id}
            >
              <MinerContextProvider miner={miner}>
                <Element name={`miner_${miner.id}`}>
                  <MinerRow
                    colored={Boolean(i % 2)}
                    rowsStyles={tableStyles?.rowsStyles ?? []}
                    cbStyle={tableStyles?.checkboxStyle ?? {}}
                    selectMiner={(m, e) => control.selectMinerByID(m.id, e)}
                    makeSelectedMiner={control.makeSelectedMiner}
                  />
                </Element>
              </MinerContextProvider>
            </Counter>
          );
        })}
      </div>
    );
}

interface MinersTableDesktopViewProps {
  readonly canMinerPorts: boolean;
  readonly control: MinersTableControl;
  readonly isMinerStatusesModal?: boolean;
  readonly tableStyles?: TableItemStyles;
  readonly total: number;
}

function MinersTableDesktopView({
  canMinerPorts,
  control,
  isMinerStatusesModal,
  tableStyles,
  total,
}: MinersTableDesktopViewProps) {
  const { formatMessage } = useIntl();

  return (
    <>
      {(control.refreshLoad || control.loading) && (
        <Loader style={{ zIndex: 99999 }} />
      )}
      {!isMinerStatusesModal && (
        <div
          className={cn(styles.table_head, {
            [styles.isAdmin]: control.isAdmin,
          })}
        >
          {control.tab === "unknown" ? (
            <UnknownTableHeader />
          ) : (
            tableStyles && (
              <TableHeader
                isAdmin={control.isAdmin}
                rowsStyles={tableStyles.rowsStyles}
                sort={control.sort}
                setSortBy={control.sortMinersBy}
                checkboxText={formatMessage({ id: "machines.table.all" })}
                checkboxTitle={control.pulseAllCheckbox}
                onSelectAll={control.selectAll}
                selectedAll={control.selectedAll}
              />
            )
          )}
          {control.isAdmin && (
            <div className={styles.addNewMiner}>
              <AddNewMiner />
              <RefreshMiners />
            </div>
          )}
        </div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {(control.tab === "excluded" || control.tab === "storage") && (
          <div className={styles.warning_container}>
            <Alert />
            <span>{formatMessage({ id: "machines.table.warning" })}</span>
          </div>
        )}
        {canMinerPorts && (
          <div className={styles.top_link__container}>
            <SmallLink className={styles.top_link} onClick={control.showPortsList}>
              {formatMessage({ id: "machines.table.user_ports_list" })}
            </SmallLink>
          </div>
        )}
      </div>
      {isMinerStatusesModal && control.miners.length > 0 && (
        <div className={styles.copyMinersMeta}>
          <SmallLink onClick={control.copyMinersMeta}>
            Copy miners meta to CSV&nbsp;
            <DownloadOutlined />
          </SmallLink>
          {control.isMetaLoading && <Loader />}
        </div>
      )}
      <DesktopContent control={control} tableStyles={tableStyles} total={total} />
    </>
  );
}

export default MinersTableDesktopView;
